import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { SiteContext } from '../../context/SiteContext';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import { TitledListItem } from '../common/CommonTypes';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { Link, useNavigate } from 'react-router-dom';
import NavigationWarningDialog from '../common/NavigationWarningDialog';
import { setCountryCode } from '../common/HelperMethods';
import { useCurrentPath } from './useCurrentPath';
import FooterLegalDocument from './FooterLegalDocument';
import { CountryIsoCodes } from '../common/Constants';

const Footer = React.memo(function Footer(): JSX.Element {
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();
  const currentPath = useCurrentPath();

  const isUkSite = site.countryCode === CountryIsoCodes.GB;

  const { journeyType, user } = site;
  const { instanceId } = user;
  const [showDialog, setShowDialog] = useState(false);
  const [showLegalDocDialog, setShowLegalDocDialog] = useState(false);
  const [path, setPath] = useState('');
  const [continueToPage, setContinueToPage] = useState(false);

  const [legalDocDialogTitle, setLegalDocDialogTitle] = useState('');
  const [legalDocDialogContent, setLegalDocDialogContent] = useState('');

  const getFooterClass = () => {
    if (journeyType === Journey.Renewals) return 'renewal-journey-footer';
    else if (journeyType === Journey.DealerJourney)
      return 'dealer-journey-footer';
  };

  const contactDetailsTitle = site.content?.footer?.contactDetailItems.title;
  const contactDetailItems = site.content?.footer?.contactDetailItems.items;

  const legalDocsTitle = site.content?.footer?.legalDocumentsItems.title;
  const legalDocsItems = site.content?.footer?.legalDocumentsItems.items;

  const tradingNameTitle = site.content?.footer?.tradingNameItems.title;
  const tradingNameItems = site.content?.footer?.tradingNameItems.items;

  const renderContactDetails = () => {
    if (contactDetailItems) {
      const items = contactDetailItems.map((item, index) =>
        renderItems(item, index)
      );

      return <>{items}</>;
    } else {
      return <></>;
    }
  };

  const renderItems = (item: TitledListItem, index: number) => {
    if (item.text.includes('@')) {
      return (
        <p className='cms' key={index}>
          <FontAwesomeIcon icon={faEnvelope} className='m05-right' />
          <a href={'mailto:' + item.text}>{item.text}</a>
        </p>
      );
    } else {
      return (
        <p className='cms' key={index}>
          <FontAwesomeIcon icon={faPhone} className='m05-right' />
          <a href={'tel:' + item.text}>{item.text}</a>
        </p>
      );
    }
  };

  const renderTradingNameItems = () => {
    if (tradingNameItems) {
      const length = tradingNameItems.length - 1;

      const items = tradingNameItems.map((item, index) => (
        <p key={index} className={getClassName(length, index)}>
          {item.text}
        </p>
      ));

      return <>{items}</>;
    } else {
      return <></>;
    }
  };

  const renderDoc = (item: TitledListItem, index: number) => {
    if (item?.file || item?.externalLink) {
      return (
        <p>
          <a
            key={`legal-doc-${index}`}
            href={item.file ?? item.externalLink}
            target='_blank'
            rel='noopener noreferrer'>
            {item.text}
          </a>
        </p>
      );
    } else if (item?.content) {
      return (
        <Link
          id={`legal-doc-${index}`}
          to='#'
          onClick={() => onClickLegalDoc(item)}>
          {item.text}
        </Link>
      );
    } else {
      return <></>;
    }
  };

  const renderDocs = () => {
    if (legalDocsItems) {
      return legalDocsItems.map((item, index) => (
        <div key={item.text}>{renderDoc(item, index)}</div>
      ));
    } else {
      return <></>;
    }
  };

  const renderLegalDocuments = () => {
    return (
      <>
        <p className='m05-bottom bold section-header m3-top'>
          {legalDocsTitle}
        </p>
        {isUkSite && (
          <p>
            <a
              href='https://www.view-privacy-policy.co.uk/Privacy-Policy'
              target='_blank'
              rel='noopener noreferrer'>
              Privacy policy
            </a>
          </p>
        )}
        {renderDocs()}
        {isUkSite && (
          <p>
            <a
              href='https://amtrustfinancial.com/vulnerability-disclosure'
              target='_blank'
              rel='noopener noreferrer'>
              Vulnerability Disclosure Policy
            </a>
          </p>
        )}
      </>
    );
  };

  const isCurrentPathInQuestionnaire =
    currentPath === PageAbsolutePath.Insurance.AboutYou ||
    currentPath === PageAbsolutePath.Insurance.DrivingHistory ||
    currentPath === PageAbsolutePath.Insurance.VehicleDetails ||
    currentPath === PageAbsolutePath.Insurance.AddressDetailsPage ||
    currentPath === PageAbsolutePath.Insurance.AdditionalDriverDetailsPage ||
    currentPath === PageAbsolutePath.Insurance.YourQuote ||
    currentPath === PageAbsolutePath.Insurance.InsuranceCoverDetailsPage ||
    currentPath === PageAbsolutePath.Insurance.ConfirmationPage ||
    currentPath === PageAbsolutePath.Insurance.OptionalCover ||
    currentPath === PageAbsolutePath.Insurance.CheckYourDetails ||
    currentPath ===
      PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice ||
    currentPath === PageAbsolutePath.Insurance.PaymentPage;

  const onClickLink = (path: string) => {
    const fullPath = setCountryCode(path, site.countryCode);
    setPath(fullPath);
    if (isCurrentPathInQuestionnaire) {
      setShowDialog(true);
    } else {
      setContinueToPage(true);
    }
  };

  useEffect(() => {
    if (continueToPage) {
      setContinueToPage(false);
      navigate(path);
    }
  }, [continueToPage]);

  const onCloseModal = () => {
    setShowDialog(false);
  };

  const onCloseLegalDocModal = () => {
    setShowLegalDocDialog(false);
  };

  const onClickLegalDoc = (item: TitledListItem) => {
    setShowLegalDocDialog(true);

    setLegalDocDialogTitle(item.text);
    setLegalDocDialogContent(item?.content ?? '');
  };

  const getClassName = (length: number, index: number) => {
    let className = '';

    if (index === length) {
      className = 'copyright m05-top';
    }

    return className;
  };

  if (
    journeyType === Journey.DealerJourney ||
    journeyType === Journey.Renewals ||
    (journeyType === Journey.Customer && CommonValidation.isEmpty(instanceId))
  ) {
    return (
      <footer className={getFooterClass()}>
        <div className='flex-row footer-container footer-font'>
          <div className='m05-bottom'>
            <p className='m05-bottom bold section-header m3-top'>
              {contactDetailsTitle}
            </p>
            {renderContactDetails()}
          </div>
          <div className='m05-bottom'>{renderLegalDocuments()}</div>
          <div className='m05-bottom'>
            <p className='bold m05-bottom section-header m3-top cms'>
              {tradingNameTitle}
            </p>
            {renderTradingNameItems()}
          </div>
        </div>
        <FooterLegalDocument
          showDialog={showLegalDocDialog}
          title={legalDocDialogTitle}
          content={legalDocDialogContent}
          onCloseModal={onCloseLegalDocModal}
        />
      </footer>
    );
  }

  if (journeyType === Journey.Insurance) {
    return (
      <div className='insurance'>
        <footer className={`footer-${site.brand.brandName}`}>
          <div className='flex-row links'>
            <Link
              id='lnkTermsOfUse'
              to='#'
              onClick={() =>
                onClickLink(PageAbsolutePath.Insurance.TermsUseAncillaryPage)
              }>
              Terms of use
            </Link>
            <Link
              id='lnkFairUse'
              to='#'
              onClick={() =>
                onClickLink(PageAbsolutePath.Insurance.FairUseNoticePage)
              }>
              Fair use notice
            </Link>
            <Link
              id='lnkUpdatePreferences'
              to='#'
              onClick={() =>
                onClickLink(PageAbsolutePath.Insurance.UpdatePreferencesPage)
              }>
              Update preferences
            </Link>
            <Link
              id='lnkSiteMap'
              to='#'
              onClick={() => onClickLink(PageAbsolutePath.Insurance.SiteMap)}>
              Site Map
            </Link>
            <Link
              id='lnkTermsOfBusiness'
              to='#'
              onClick={() =>
                onClickLink(
                  PageAbsolutePath.Insurance.BusinessTermsAncillaryPage
                )
              }>
              Terms of Business
            </Link>
          </div>
          <p>
            The offer of insurance is subject to eligibility and underwriting
            criteria. All cover is subject to the terms and conditions provided
            with the policy, the
            <Link
              id='lnkPolicyDocumentInText'
              className='in-text'
              to='#'
              onClick={() =>
                onClickLink(PageAbsolutePath.Insurance.PolicyDocuments)
              }>
              Policy Document
            </Link>
            and the
            <Link
              id='lnkTermsOfBusinessInText'
              className='in-text'
              to='#'
              onClick={() =>
                onClickLink(
                  PageAbsolutePath.Insurance.BusinessTermsAncillaryPage
                )
              }>
              Terms of Business.
            </Link>
          </p>
          <p>
            {`${site.brand.brandName} Insurance is arranged by Car Care Plan Limited, which is
                    registered in England, Company No. 850195 at Jubilee House, 5 Midpoint
                    Business Park, Thornbury, West Yorkshire, BD3 7AG and authorised and
                    regulated by the Financial Conduct Authority, No. 309268. Car Care
                    Plan Limited arranges policies from a panel of insurers, details are
                    available on request. To understand how we may process your
                    information read our`}
            <Link
              id='lnkFairUseInText'
              className='in-text'
              to='#'
              onClick={() =>
                onClickLink(PageAbsolutePath.Insurance.FairUseNoticePage)
              }>
              Fair Use Notice.
            </Link>
          </p>
        </footer>
        <NavigationWarningDialog
          showDialog={showDialog}
          path={path}
          onCloseModal={onCloseModal}
          brandName={site.brand.brandName}
        />
      </div>
    );
  }

  return <></>;
});

export default Footer;
